import first from 'lodash/first';
import get from 'lodash/get';
import last from 'lodash/last';

import { EVENT_INITIATORS, INTERACTION_TYPES } from '@zocdoc/frontend-metrics';

import { useViewedEffect } from './useViewedEffect';
import { getAvailabilityTypeDisplayed } from './availability';

/* Builds the TimesgridViewedEvent using data from a TimeslotsView
 * to record what the user actually sees on the page
 */
export const buildTimesgridVisibleTimesEventDetails = ({
    isMobile,
    rowCount = null,
    times = [],
}) => {
    if (isMobile) {
        // MobileTimeslotView doesn't have rows
        rowCount = null;
    }
    const visibleTimes = times.reduce(
        (acc, { date, timeslots, timesgridId }) => {
            const endIndex =
                typeof rowCount === 'number' && timeslots.length > rowCount
                    ? // If the number of timeslots is greater than rowCount,
                      // reduce the timeslot count by 1 because
                      // the last slot is replaced by a 'more' button.
                      rowCount - 1
                    : rowCount;
            const visibleTimeslots = endIndex
                ? timeslots.slice(0, endIndex)
                : timeslots;

            acc[date] = {
                firstAppointment: first(visibleTimeslots)?.startTime || null,
                lastAppointment: last(visibleTimeslots)?.startTime || null,
                timesgridId,
                timeslotCount: visibleTimeslots.length,
            };
            return acc;
        },
        {}
    );

    return { visibleTimes };
};

export const buildNextOrPreviousMetrics = (next = true) => ({
    action: next ? 'Click Next Days' : 'Click Previous Days',
    element: next ? 'NextDays' : 'PreviousDays',
    initiator: EVENT_INITIATORS.USER,
    interactionType: INTERACTION_TYPES.CLICK,
});

// View Metrics: if the timesgrid is in view, and we make an API call
// (i.e. a change in the times object) and data finished loading for that
// action, we record a TimesgridViewed event
// This should happen on
//     1.  Page load, already into view
//     2.  Loaded and scrolled into view (changes visibilityStatus)
//     3. 'Next/Prev Button' was clicked (changes times/nextAvailability)
//     4. 'Next Availability' was clicked (changes times/nextAvailability)
// Be wary to not change the passed arguments to this effect
// or it will send additional events
export function useTimesgridViewedMetric({
    initialRowCount,
    locationId,
    nextAvailability,
    providerId,
    searchRequestId,
    sendMetric,
    startDate,
    times,
}) {
    const container = useViewedEffect(() => {
        const visibleTimes = buildTimesgridVisibleTimesEventDetails({
            times,
            rowCount: initialRowCount,
        });

        const availabilityTypeDisplayed = getAvailabilityTypeDisplayed(times);
        const firstAvailableTimeslotLocal = get(
            nextAvailability,
            'startTime',
            null
        );

        sendMetric({
            action: 'Timesgrid Viewed',
            initiator: EVENT_INITIATORS.USER,
            interactionType: INTERACTION_TYPES.VIEW,
            eventDetails: {
                ...visibleTimes,
                availabilityTypeDisplayed,
                firstAvailableTimeslotLocal,
                searchRequestId,
                timeslotRequestStartDate: startDate,
                providerId,
                locationId,
            },
        });
    });

    return container;
}
